import { useEffect, useState } from "react";
import { Icon } from "../../components/Icon";
import { api } from "../../lib/api";
import { useClient } from "../../lib/useClients";
import { ClientOrdersTab } from "./ClientOrdersTab";
import { ClientPaymentsTab } from "./ClientPaymentsTab";
import { ClientDocsTab } from "./ClientDocsTab";
import { ClientNotesTab } from "./ClientNotesTab";
import { ClientLinksTab } from "./ClientLinksTab";

type Tab = "orders" | "payments" | "docs" | "notes" | "links";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "orders", label: "Заказы", icon: "orders" },
  { id: "payments", label: "Платежи", icon: "money" },
  { id: "docs", label: "Документы", icon: "image" },
  { id: "notes", label: "Заметки", icon: "edit" },
  { id: "links", label: "Связи", icon: "users" },
];

const STATUS_PILL: Record<string, { cls: string; label: string }> = {
  vip: { cls: "vip", label: "⭐ VIP" },
  active: { cls: "active", label: "Активный" },
  inactive: { cls: "gray", label: "Неактивен" },
};
const FALLBACK_PILL = { cls: "gray", label: "Неактивен" };

/**
 * ClientDrawer — right-side client card. Stacks ABOVE OrderDrawer
 * (`.client-overlay` carries the higher z-index in client_drawer.css), so it
 * can be opened from OrderDrawer / ClientsLanding / delivery flow without
 * unmounting the underlying drawer.
 */
export function ClientDrawer({
  clientId,
  onClose,
  onOpenClient,
  onOpenOrder,
}: {
  clientId: string;
  onClose: () => void;
  onOpenClient?: (id: string) => void;
  onOpenOrder?: (row: { id: string; type: string }) => void;
}) {
  const [tab, setTab] = useState<Tab>("orders");
  const { data: c, isLoading } = useClient(clientId);

  // Passport is hidden by default. «Показать» fetches the full value from the
  // logged reveal endpoint (each call writes an immutable PiiAccessLog row).
  const [passport, setPassport] = useState<{
    passportSeries: string | null;
    passportNumber: string | null;
  } | null>(null);
  const [passportLoading, setPassportLoading] = useState(false);
  const [passportError, setPassportError] = useState(false);

  // Reset the revealed passport when switching to another client.
  useEffect(() => {
    setPassport(null);
    setPassportLoading(false);
    setPassportError(false);
  }, [clientId]);

  async function revealPassport() {
    setPassportLoading(true);
    setPassportError(false);
    try {
      const r = await api<{ passportSeries: string | null; passportNumber: string | null }>(
        `/admin/clients/${clientId}/passport`,
      );
      setPassport(r);
    } catch {
      setPassportError(true);
    } finally {
      setPassportLoading(false);
    }
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!c) {
    return (
      <div className="drawer-overlay client-overlay" onClick={onClose}>
        <div
          className="drawer client-drawer"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="drawer-head">
            <button className="btn-admin ghost" onClick={onClose}>
              <Icon name="close" />
            </button>
            <h2>{isLoading ? "Загрузка…" : "Клиент не найден"}</h2>
          </div>
        </div>
      </div>
    );
  }

  const pill = STATUS_PILL[c.status] ?? FALLBACK_PILL;

  return (
    <div className="drawer-overlay client-overlay" onClick={onClose}>
      <div
        className="drawer client-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cd-hero">
          <button className="cd-close" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>

          <div className="cd-hero-top">
            <div className={"cd-avatar status-" + c.status}>{c.initials}</div>
            <div className="cd-hero-meta">
              <div className="cd-hero-row1">
                <h2 className="cd-name">{c.name}</h2>
                <span className={"cd-status-pill " + pill.cls}>
                  {pill.label}
                </span>
              </div>
              <p className="cd-hero-row2">
                <span className="mono">{c.id}</span>
                {c.city && (
                  <>
                    <span className="cd-dot" />
                    <span>{c.city}</span>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="cd-contacts">
            {/* One-click open of the client's Telegram profile: by @username when
                known, else by chat id (tg://user?id=…). Always shown. */}
            <a
              className="cd-contact"
              href={
                c.tgUsername
                  ? `https://t.me/${c.tgUsername.replace(/^@+/, "")}`
                  : `tg://user?id=${c.id}`
              }
              target="_blank"
              rel="noreferrer"
              title="Открыть профиль в Telegram"
              style={{ textDecoration: "none", color: "inherit", cursor: "pointer" }}
            >
              <span className="cd-contact-ico tg">TG</span>
              <div>
                <p className="cd-contact-l">{c.tgUsername ?? c.id}</p>
                <p className="cd-contact-s">Telegram</p>
              </div>
            </a>
            {c.phone && (
              <div className="cd-contact">
                <span className="cd-contact-ico phone">☎</span>
                <div>
                  <p className="cd-contact-l">{c.phone}</p>
                  <p className="cd-contact-s">телефон</p>
                </div>
              </div>
            )}
            {c.whatsapp && (
              <div className="cd-contact">
                <span className="cd-contact-ico wa">WA</span>
                <div>
                  <p className="cd-contact-l">{c.whatsapp}</p>
                  <p className="cd-contact-s">WhatsApp</p>
                </div>
              </div>
            )}
            {c.instagram && (
              <div className="cd-contact">
                <span className="cd-contact-ico ig">IG</span>
                <div>
                  <p className="cd-contact-l">{c.instagram}</p>
                  <p className="cd-contact-s">Instagram</p>
                </div>
              </div>
            )}
            {/* Паспорт — скрыт по умолчанию. «Показать» дёргает логируемый
                reveal-эндпоинт (каждый вызов = запись в иммутабельный аудит). */}
            <div className="cd-contact">
              <span
                className="cd-contact-ico"
                style={{
                  background: "var(--mnp-bg-alt)",
                  border: "1px solid var(--mnp-border)",
                  color: "var(--mnp-fg-2)",
                }}
              >
                №
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="cd-contact-l">
                  {passport
                    ? `${passport.passportSeries ?? ""} ${passport.passportNumber ?? ""}`.trim() ||
                      "—"
                    : passportError
                      ? "ошибка загрузки"
                      : (c.passportMasked ?? "—")}
                </p>
                <p className="cd-contact-s">Паспорт</p>
              </div>
              {c.passportMasked &&
                (passport ? (
                  <button
                    type="button"
                    onClick={() => setPassport(null)}
                    style={{
                      marginLeft: "auto",
                      flexShrink: 0,
                      fontSize: 12,
                      padding: "4px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--mnp-border)",
                      background: "var(--mnp-bg-alt)",
                      color: "var(--mnp-fg-1)",
                      cursor: "pointer",
                    }}
                  >
                    Скрыть
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={revealPassport}
                    disabled={passportLoading}
                    style={{
                      marginLeft: "auto",
                      flexShrink: 0,
                      fontSize: 12,
                      padding: "4px 10px",
                      borderRadius: 8,
                      border: "1px solid var(--mnp-border)",
                      background: "var(--mnp-bg-alt)",
                      color: "var(--mnp-fg-1)",
                      cursor: passportLoading ? "default" : "pointer",
                    }}
                  >
                    {passportLoading ? "…" : "Показать"}
                  </button>
                ))}
            </div>
          </div>

          <div className="cd-kpi">
            <div>
              <span className="cd-kpi-v">{c.stats.orders}</span>
              <span className="cd-kpi-l">заказов</span>
            </div>
            <div>
              <span className="cd-kpi-v">{c.stats.turnover}</span>
              <span className="cd-kpi-l">оборот</span>
            </div>
            <div>
              <span className="cd-kpi-v">{c.stats.avgCheck ?? "—"}</span>
              <span className="cd-kpi-l">средний чек</span>
            </div>
          </div>
        </div>

        <div className="cd-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-active={tab === t.id}
              onClick={() => setTab(t.id)}
            >
              <Icon name={t.icon as never} size={13} /> {t.label}
            </button>
          ))}
        </div>

        <div className="cd-body">
          {tab === "orders" && (
            <ClientOrdersTab clientId={c.id} onOpenOrder={onOpenOrder} />
          )}
          {tab === "payments" && <ClientPaymentsTab clientId={c.id} />}
          {tab === "docs" && <ClientDocsTab clientId={c.id} />}
          {tab === "notes" && <ClientNotesTab clientId={c.id} />}
          {tab === "links" && (
            <ClientLinksTab clientId={c.id} onOpenClient={onOpenClient} />
          )}
        </div>
      </div>
    </div>
  );
}
